if (!customElements.get('quick-add-modal')) {
  const quickAddAssetUrl = document.currentScript?.src ? new URL(document.currentScript.src) : null;
  const trustedComponentScripts = new Map([
    ['media-gallery.js', 'media-gallery'],
    ['product-model.js', 'product-model'],
  ]);
  const trustedScriptLoads = new Map();

  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    }

    hide(preventFocus = false) {
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      const spinner = opener.querySelector('.loading-overlay__spinner');
      const statusContainer = opener.closest('modal-opener') || opener.parentElement;
      let loadError = statusContainer?.querySelector('[data-quick-add-load-error]');
      if (!loadError && statusContainer) {
        loadError = document.createElement('span');
        loadError.className = 'quick-add__load-error caption';
        loadError.dataset.quickAddLoadError = '';
        loadError.setAttribute('role', 'alert');
        loadError.setAttribute('aria-live', 'assertive');
        loadError.hidden = true;
        statusContainer.appendChild(loadError);
      }

      if (loadError) {
        loadError.hidden = true;
        loadError.textContent = '';
      }
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      spinner?.classList.remove('hidden');

      fetch(opener.getAttribute('data-product-url'))
        .then((response) => {
          if (!response || typeof response.text !== 'function') throw new Error('Quick Add returned an invalid response');
          if (response.ok === false) throw new Error(`Quick Add returned ${response.status}`);
          return response.text();
        })
        .then(async (responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
          if (!this.productElement || !this.modalContent) throw new Error('Quick Add product markup is missing');
          this.preventDuplicatedIDs();
          this.removeDOMElements();
          if (this.productElement.querySelector('media-gallery')) this.productElement.querySelector('media-gallery').dataset.disablePrepend = 'false';
          await this.setInnerHTML(this.modalContent, this.productElement.innerHTML);
          this.initializePersonalization();

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
          super.show(opener);
        })
        .catch((error) => {
          console.warn('[ZORIA] Quick Add could not load.', error);
          if (loadError) {
            loadError.textContent = 'לא הצלחנו לטעון את אפשרויות המוצר כרגע. נסו שוב בעוד רגע.';
            loadError.hidden = false;
          }
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          spinner?.classList.add('hidden');
        });
    }

    async setInnerHTML(element, html) {
      const template = document.createElement('template');
      template.innerHTML = html;
      const componentScripts = new Map();

      const removeExecutableScripts = (root) => {
        root.querySelectorAll('script').forEach((scriptTag) => {
          const type = (scriptTag.getAttribute('type') || '').trim().toLowerCase();
          const isInlineJson = !scriptTag.hasAttribute('src')
            && (type === 'application/json' || type === 'application/ld+json');

          if (isInlineJson) return;

          const componentScript = this.getTrustedComponentScript(scriptTag);
          if (componentScript) componentScripts.set(componentScript.customElement, componentScript);
          scriptTag.remove();
        });

        root.querySelectorAll('template').forEach((nestedTemplate) => {
          removeExecutableScripts(nestedTemplate.content);
        });
      };

      removeExecutableScripts(template.content);
      element.replaceChildren(template.content);
      await Promise.all(Array.from(componentScripts.values(), (script) => this.loadTrustedComponentScript(script)));
    }

    getTrustedComponentScript(scriptTag) {
      if (!quickAddAssetUrl || !scriptTag.hasAttribute('src')) return null;

      let scriptUrl;
      try {
        scriptUrl = new URL(scriptTag.getAttribute('src'), document.baseURI);
      } catch (_error) {
        return null;
      }

      const assetName = scriptUrl.pathname.slice(scriptUrl.pathname.lastIndexOf('/') + 1);
      const customElement = trustedComponentScripts.get(assetName);
      if (!customElement || scriptUrl.username || scriptUrl.password) return null;

      const trustedDirectory = quickAddAssetUrl.pathname.slice(0, quickAddAssetUrl.pathname.lastIndexOf('/') + 1);
      const scriptDirectory = scriptUrl.pathname.slice(0, scriptUrl.pathname.lastIndexOf('/') + 1);
      if (scriptUrl.origin !== quickAddAssetUrl.origin || scriptDirectory !== trustedDirectory) return null;

      return { src: scriptUrl.href, customElement };
    }

    loadTrustedComponentScript({ src, customElement }) {
      if (customElements.get(customElement)) return Promise.resolve();
      if (trustedScriptLoads.has(src)) return trustedScriptLoads.get(src);

      const load = new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = src;
        script.addEventListener('load', resolve, { once: true });
        script.addEventListener('error', resolve, { once: true });
        document.head.appendChild(script);
      }).finally(() => trustedScriptLoads.delete(src));

      trustedScriptLoads.set(src, load);
      return load;
    }

    initializePersonalization() {
      const personalization = this.modalContent.querySelector(
        '.zoria-file-upload, custom-product-field, product-info[data-personalization-optional="true"], [data-zoria-personalization-summary]'
      );
      if (!personalization) return;

      // The theme's personalization asset is already trusted and loaded globally; this hook scopes its initializer to the new modal content.
      this.modalContent.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
      if (!variantPicker) return;

      variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const stickyAtc = this.productElement.querySelector('sticky-atc');
      if (stickyAtc) stickyAtc.remove();

      const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach(modal => modal.remove());
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      
      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }
  });
}
